import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title?: string
  description?: string
  image?: string
  type?: 'website' | 'article' | 'product'
  canonical?: string
  noindex?: boolean
  schema?: Record<string, unknown>
}

export function SEO({
  title,
  description,
  image = '/og-image.jpg',
  type = 'website',
  canonical,
  noindex = false,
  schema,
}: SEOProps) {
  const { i18n } = useTranslation('common')
  const location = useLocation()
  const currentLang = i18n.language as 'it' | 'en'
  const alternateLang = currentLang === 'it' ? 'en' : 'it'

  // Site configuration
  const siteName = 'Fiorucci Salumi'
  const siteUrl = 'https://fioruccisalumi.it'

  // Default values
  const defaultTitle = 'Fiorucci Salumi | Ogni fetta, un momento che resta'
  const defaultDescription =
    currentLang === 'it'
      ? 'Dal 1850, Fiorucci produce salumi di qualità superiore. Scopri la nostra gamma di mortadelle, prosciutti, salami e specialità italiane.'
      : 'Since 1850, Fiorucci has been producing superior quality cured meats. Discover our range of mortadella, hams, salami and Italian specialties.'

  const pageTitle = title ? `${title} | ${siteName}` : defaultTitle
  const pageDescription = description || defaultDescription
  const pageUrl = `${siteUrl}${location.pathname}`
  const pageImage = image.startsWith('http') ? image : `${siteUrl}${image}`

  // JSON-LD Structured Data
  const defaultSchema = {
    '@context': 'https://schema.org',
    '@type': type === 'website' ? 'WebSite' : type === 'article' ? 'Article' : 'Organization',
    name: siteName,
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: pageDescription,
    inLanguage: currentLang === 'it' ? 'it-IT' : 'en-US',
    ...(type === 'website' && {
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    }),
    ...(type === 'article' && {
      author: {
        '@type': 'Organization',
        name: siteName,
      },
      publisher: {
        '@type': 'Organization',
        name: siteName,
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/logo.png`,
        },
      },
    }),
  }

  const jsonLd = schema || defaultSchema

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}
      {!canonical && <link rel="canonical" href={pageUrl} />}

      {/* Hreflang Tags for Internationalization */}
      <link rel="alternate" hrefLang="it-IT" href={`${siteUrl}/it${location.pathname.replace(/^\/(it|en)/, '')}`} />
      <link rel="alternate" hrefLang="en-US" href={`${siteUrl}/en${location.pathname.replace(/^\/(it|en)/, '')}`} />
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}/it${location.pathname.replace(/^\/(it|en)/, '')}`} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={currentLang === 'it' ? 'it_IT' : 'en_US'} />
      <meta property="og:locale:alternate" content={alternateLang === 'it' ? 'it_IT' : 'en_US'} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#4E2118" />
      <meta name="msapplication-TileColor" content="#4E2118" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

// Convenience hooks for common page types
export function useSEO() {
  const { i18n } = useTranslation('common')
  const location = useLocation()

  const getLocalizedUrl = (path: string) => {
    const lang = i18n.language
    return `/${lang}${path}`
  }

  return {
    i18n,
    location,
    getLocalizedUrl,
  }
}

// Pre-configured SEO for common page types
export function HomeSEO() {
  const { t } = useTranslation('common')

  return (
    <SEO
      title={t('nav.home')}
      description={t('seo.homeDescription')}
      type="website"
    />
  )
}

export function ProductSEO({ productName }: { productName: string }) {
  const { t } = useTranslation('common')

  return (
    <SEO
      title={productName}
      description={t('seo.productDescription', { product: productName })}
      type="product"
    />
  )
}
